import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";

import {
  useSelector,
} from "react-redux";

import {
  ShoppingBag,
} from "lucide-react";

import {
  useCart,
} from "../../components/CartContext";

import "./css/Header.css";


function Header() {

  /* =====================================================
     ROUTER
  ===================================================== */

  const navigate = useNavigate();

  const location = useLocation();

  const {
    restaurantId,
    tableId,
  } = useParams();


  /* =====================================================
     MOBILE MENU
  ===================================================== */

  const [
    menuOpen,
    setMenuOpen,
  ] = useState(false);


  /* =====================================================
     CART
  ===================================================== */

  const {
    cartCount,
  } = useCart();


  /* =====================================================
     RESTAURANT NAME
  ===================================================== */

  const restaurantName =
    useSelector(
      (state) =>
        state.menu.restaurantName
    ) || "YUM!";


  /* =====================================================
     BASE HOME PATH
  ===================================================== */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  /* =====================================================
     CLOSE MOBILE MENU
  ===================================================== */

  const closeMenu = () => {
    setMenuOpen(false);
  };


  /* =====================================================
     HOME
  ===================================================== */

  const goToHome = () => {

    closeMenu();


    /*
      Already on Home:
      smoothly scroll to top.
    */

    if (
      location.pathname === homePath
    ) {

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return;
    }


    /*
      Coming from Cart,
      Checkout,
      Payment,
      Order, etc.
    */

    navigate(homePath);

  };


  /* =====================================================
     CART
  ===================================================== */

  const goToCart = () => {

    closeMenu();

    navigate(
      `${homePath}/cart`
    );

  };


  /* =====================================================
     SECTION NAVIGATION
  ===================================================== */

  const scrollToSection = (
    sectionId
  ) => {

    closeMenu();


    /*
      Already on Home.

      Find the Theme 9 section and
      scroll directly to it.
    */

    if (
      location.pathname === homePath
    ) {

      const element =
        document.getElementById(
          sectionId
        );


      if (element) {

        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

      }

      return;
    }


    /*
      If user is currently on:
      Cart
      Checkout
      Order
      Payment
      Success
      etc.

      Return to Home first and
      preserve target section
      using URL hash.
    */

    navigate(
      `${homePath}#${sectionId}`
    );

  };


  /* =====================================================
     HANDLE HASH AFTER RETURNING HOME
  ===================================================== */

  useEffect(() => {

    /*
      Only process section hash
      when we are actually on Home.
    */

    if (
      location.pathname !== homePath
    ) {
      return;
    }


    const sectionId =
      location.hash.replace(
        "#",
        ""
      );


    if (!sectionId) {
      return;
    }


    /*
      Give HomeScreen time to render
      menu data and sections.
    */

    const timer =
      setTimeout(() => {

        const element =
          document.getElementById(
            sectionId
          );


        if (element) {

          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });

        }

      }, 150);


    return () => {
      clearTimeout(timer);
    };

  }, [
    location.pathname,
    location.hash,
    homePath,
  ]);


  /* =====================================================
     MOBILE MENU TOGGLE
  ===================================================== */

  const toggleMobileMenu = () => {

    setMenuOpen(
      (previous) =>
        !previous
    );

  };


  /* =====================================================
     CLOSE MOBILE MENU AFTER ROUTE CHANGE
  ===================================================== */

  useEffect(() => {

    setMenuOpen(false);

  }, [
    location.pathname,
  ]);


  /* =====================================================
     PREVENT BODY SCROLL WHEN MOBILE MENU IS OPEN
  ===================================================== */

  useEffect(() => {

    if (!menuOpen) {
      document.body.style.overflow = "";

      return undefined;
    }


    document.body.style.overflow =
      "hidden";


    return () => {

      document.body.style.overflow =
        "";

    };

  }, [menuOpen]);


  /* =====================================================
     HEADER
  ===================================================== */

  return (

    <header className="theme9-header">


      <div className="theme9-header-inner">


        {/* =================================================
            LOGO
        ================================================= */}

        <button
          type="button"
          className="theme9-logo"
          onClick={goToHome}
          aria-label="Go to home"
        >

          <span className="theme9-logo-text">
            {restaurantName}
          </span>


          <span className="theme9-logo-dot">
            .
          </span>

        </button>


        {/* =================================================
            NAVIGATION
        ================================================= */}

        <nav
          className={
            `theme9-navigation ${
              menuOpen
                ? "theme9-navigation-open"
                : ""
            }`
          }
        >


          {/* HOME */}

          <button
            type="button"
            onClick={goToHome}
          >
            Home
          </button>


          {/* MENU */}

          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme9-menu"
              )
            }
          >
            Menu
          </button>


          {/* ABOUT */}

          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme9-about"
              )
            }
          >
            About
          </button>


          {/* POPULAR */}

          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme9-popular"
              )
            }
          >
            Popular
          </button>


          {/* CONTACT */}

          <button
            type="button"
            onClick={() =>
              scrollToSection(
                "theme9-contact"
              )
            }
          >
            Contact
          </button>


        </nav>


        {/* =================================================
            RIGHT SIDE
        ================================================= */}

        <div className="theme9-header-actions">


          {/* =================================================
              CART
          ================================================= */}

          <button
            type="button"
            className="theme9-header-cart"
            onClick={goToCart}
            aria-label={
              `Open cart. ${
                cartCount || 0
              } items`
            }
          >

            <ShoppingBag
              className="
                theme9-cart-icon
              "
              size={19}
            />


            <span className="
              theme9-cart-text
            ">
              Cart
            </span>


            <span className="
              theme9-header-cart-count
            ">
              {cartCount || 0}
            </span>

          </button>


          {/* =================================================
              MOBILE MENU BUTTON
          ================================================= */}

          <button
            type="button"
            className={
              `theme9-mobile-menu-button ${
                menuOpen
                  ? "theme9-mobile-menu-active"
                  : ""
              }`
            }
            onClick={
              toggleMobileMenu
            }
            aria-label={
              menuOpen
                ? "Close navigation"
                : "Open navigation"
            }
            aria-expanded={
              menuOpen
            }
          >

            <span></span>

            <span></span>

            <span></span>

          </button>


        </div>

      </div>


      {/* =====================================================
          MOBILE OVERLAY

          Clicking outside navigation
          closes mobile menu.
      ===================================================== */}

      {menuOpen && (

        <button
          type="button"
          className="
            theme9-mobile-overlay
          "
          onClick={closeMenu}
          aria-label="
            Close navigation
          "
        />

      )}


    </header>

  );

}


export default Header;